import { blogsData } from "../../utils/constants";
import { Carousel } from "@mantine/carousel";
import { motion } from "framer-motion";
import { FiArrowRight, FiCalendar } from "react-icons/fi";

const Blogs = () => {
  const data = blogsData;

  // Extract plain text from HTML description
  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <section className="relative bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 overflow-x-hidden px-p83 py-10 500:py-14 min-h-[90vh] flex flex-col justify-center" id="blogs">
      {/* Background accent */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-primary-500/8 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 pointer-events-none" />
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-200/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto w-full">
        {/* Header Section */}
        <motion.div
          className="text-center mb-8 500:mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500/20 to-primary-600/10 border border-primary-500/30 rounded-full mb-4 backdrop-blur-sm"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
            <span className="text-primary-300 font-poppins-semibold text-sm">
              Latest Articles
            </span>
          </motion.div>

          <h2 className="text-2xl 350:text-3xl 600:text-4xl 700:text-5xl font-merry-bold tracking-tight mb-3">
            Our Latest{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-primary-500 font-bold">Blogs</span>
              <motion.span
                className="absolute bottom-1 left-0 right-0 h-2 bg-primary-500/20 -z-0 rounded-full"
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </h2>

          <motion.p
            className="text-neutral-300 text-sm 500:text-base max-w-2xl mx-auto font-poppins-regular"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Stay updated with the latest insights, trends, and knowledge from our
            expert team
          </motion.p>
        </motion.div>

        {/* Carousel Section */}
        <motion.div
          className="pb-8"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Carousel
            slidesToScroll={1}
            slideSize={{ base: "100%", sm: "85%", md: "45%", lg: "38%", xl: "33%" }}
            slideGap="xl"
            controlSize={40}
            align="start"
            loop
            dragFree
            className="overflow-visible"
            styles={{
              root: {
                overflow: "visible",
              },
              viewport: {
                overflow: "visible",
                paddingBottom: "2rem",
              },
              container: {
                overflow: "visible",
              },
              control: {
                backgroundColor: "rgba(31, 41, 55, 0.8)",
                border: "2px solid rgba(0, 120, 209, 0.3)",
                color: "rgb(96, 165, 250)",
                backdropFilter: "blur(8px)",
                "&:hover": {
                  backgroundColor: "rgba(0, 120, 209, 0.2)",
                  borderColor: "rgba(0, 120, 209, 0.5)",
                  color: "rgb(59, 130, 246)",
                },
              },
            }}
          >
            {data.length > 0 ? (
              data.map(({ _id, image, title, description, tags }, index) => (
                <Carousel.Slide key={_id}>
                  <motion.div
                    className="group h-full"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <motion.div
                      className="bg-gradient-to-br from-neutral-800/95 via-neutral-800 to-neutral-900/95 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:shadow-primary-500/20 transition-all duration-500 border border-neutral-700/50 backdrop-blur-sm hover:border-primary-500/50 h-full flex flex-col cursor-pointer group/card"
                      whileHover={{ y: -12, scale: 1.02 }}
                      onClick={() => {
                        window.open(`/blog/${_id}`, "_blank", "noopener,noreferrer");
                      }}
                    >
                      {/* Gradient overlay on hover */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 z-0 pointer-events-none"
                        initial={false}
                      />
                      {/* Image Container */}
                      <div className="relative overflow-hidden h-48 500:h-56">
                        <motion.img
                          src={image}
                          alt={title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        {/* Date Badge */}
                        <div className="absolute top-4 left-4 bg-gradient-to-br from-neutral-800/95 to-neutral-900/95 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-2 border border-neutral-700/50 shadow-lg">
                          <FiCalendar className="w-3 h-3 text-primary-400" />
                          <span className="text-xs font-poppins-medium text-neutral-200">
                            Latest
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5 500:p-6 flex-1 flex flex-col">
                        {/* Tags */}
                        <div className="flex gap-2 flex-wrap mb-3 relative z-10">
                          {tags.slice(0, 2).map((tag, i) => (
                            <span
                              key={i}
                              className="px-3 py-1.5 bg-gradient-to-r from-primary-500/20 to-primary-600/10 border border-primary-500/30 text-primary-300 rounded-full text-xs font-poppins-medium backdrop-blur-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        {/* Title */}
                        <h3 className="text-lg 400:text-xl 500:text-2xl font-poppins-bold tracking-tight line-clamp-2 mb-3 text-neutral-100 group-hover/card:text-primary-400 transition-colors duration-300 min-h-[3rem] relative z-10">
                          {title}
                        </h3>

                        {/* Description */}
                        <p className="text-sm 500:text-base text-neutral-300 font-poppins-regular leading-relaxed line-clamp-3 mb-4 flex-1 relative z-10">
                          {stripHtml(description).substring(0, 120)}...
                        </p>

                        {/* Read More */}
                        <div className="flex items-center gap-2 text-primary-400 font-poppins-semibold text-sm group-hover/card:text-primary-300 transition-colors duration-300 mt-auto pt-4 border-t border-neutral-700/50 relative z-10">
                          <span>Read Article</span>
                          <motion.div
                            className="group-hover/card:translate-x-1 transition-transform duration-300"
                            initial={false}
                          >
                            <FiArrowRight className="w-4 h-4" />
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                </Carousel.Slide>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="font-poppins-semibold text-xl text-neutral-500">
                  No blogs available
                </p>
              </div>
            )}
          </Carousel>
        </motion.div>
      </div>
    </section>
  );
};

export default Blogs;
